// Results column React island.
//
// One CSS grid container; all six dimension cards always rendered as
// siblings. `expandedDimension` drives the grid template:
//
//   default (nothing expanded):  3 columns x 2 rows, cards flow naturally.
//   one expanded:                 6 columns x 2 rows. The 5 non-expanded
//                                 cards pack left into columns 1-5 (in
//                                 DIMENSIONS order, expanded card skipped).
//                                 The expanded card spans the full width
//                                 on row 2. Column 6 is always empty — the
//                                 hole sits at the right edge regardless
//                                 of which card is expanded.
//
// Motion's `layout` prop on each card animates position+size as the grid
// template flips. Click any card to expand; click the expanded card to
// collapse back to the grid.
//
// Engine data flows in via the Shiny -> React custom-message bridge
// (state/shiny_bridge.ts); Plumber still seeds the baseline on mount as
// a fallback path for when Shiny isn't bridged yet.

import { useEffect } from "react";
import {
  QueryClient,
  QueryClientProvider,
  useQuery,
} from "@tanstack/react-query";
import { LazyMotion, domMax } from "motion/react";
import { fetchScenarios } from "../lib/api";
import { DIMENSIONS, LOSS_TEXT_COLOUR } from "../lib/dimensions";
import type { DimensionKey } from "../lib/dimensions";
import { useResultsStore } from "../state/results_store";
import { useResetAllSignal } from "../state/reset_signal";
import { useShinyEngineBridge } from "../state/shiny_bridge";
import { useSimulatingSignal } from "../state/simulating_signal";
import { useUrlSync } from "../state/url_sync";
import { DimensionCard } from "./dimension_card";
import type { CardState } from "./dimension_card";
import { SimulatingOverlay } from "./simulating_overlay";
import { RestoreNotice } from "./restore_notice";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

function ResultsShell(): JSX.Element {
  useUrlSync();
  useShinyEngineBridge();
  useSimulatingSignal();
  // Page-level "Reset all" snaps the results back to baseline immediately,
  // without waiting for a Simulate click (#132).
  useResetAllSignal(useResultsStore((s) => s.resetToBaseline));

  const expandedDimension = useResultsStore((s) => s.expandedDimension);
  const engineOutput = useResultsStore((s) => s.engineOutput);
  const baselineOutput = useResultsStore((s) => s.baselineOutput);
  const isModified = useResultsStore((s) => s.isModified);
  const isSimulating = useResultsStore((s) => s.isSimulating);
  const engineError = useResultsStore((s) => s.engineError);
  const setExpandedDimension = useResultsStore((s) => s.setExpandedDimension);
  const setEngineOutput = useResultsStore((s) => s.setEngineOutput);
  const setBaselineOutput = useResultsStore((s) => s.setBaselineOutput);

  const { data, error, isLoading } = useQuery({
    queryKey: ["scenarios", "baseline"],
    queryFn: ({ signal }) => fetchScenarios([{}], { signal }),
  });

  useEffect(() => {
    if (data?.outputs?.[0]) {
      setBaselineOutput(data.outputs[0]);
      setEngineOutput(data.outputs[0]);
    }
  }, [data, setBaselineOutput, setEngineOutput]);

  if (isLoading) return <p>Loading from Plumber…</p>;
  if (error) {
    return (
      <p style={{ color: LOSS_TEXT_COLOUR }}>
        Plumber API error: {(error as Error).message}
      </p>
    );
  }
  if (!engineOutput) return <p>No data yet.</p>;

  return (
    <div className="beamm-results-island">
      <SimulatingOverlay active={isSimulating} />
      {engineError && (
        <div
          className="beamm-engine-error"
          role="alert"
          data-testid="engine-error"
          style={{ color: LOSS_TEXT_COLOUR }}
        >
          {engineError}
        </div>
      )}
      <div
        className={`beamm-dimension-grid ${expandedDimension ? "beamm-dimension-grid--expanded" : ""}`}
        data-testid="results-column-island"
        data-modified={String(isModified)}
      >
        {DIMENSIONS.map((dim) => {
          const state: CardState =
            dim.key === expandedDimension
              ? "expanded"
              : expandedDimension
                ? "strip"
                : "grid";
          const gridStyle = computeGridPlacement(
            state,
            dim.key,
            expandedDimension,
          );
          return (
            <div
              key={dim.key}
              className="beamm-dimension-slot"
              style={gridStyle}
            >
              <DimensionCard
                dimension={dim}
                state={state}
                engineOutput={engineOutput}
                baselineOutput={baselineOutput}
                isModified={isModified}
                onExpand={() => setExpandedDimension(dim.key)}
                onCollapse={() => setExpandedDimension(null)}
              />
            </div>
          );
        })}
      </div>
      {!expandedDimension && (
        <p
          className="beamm-results-empty-state"
          data-testid="results-empty-state"
        >
          The above boxes are your navigation menu. Click on them to see more
          details.
        </p>
      )}
    </div>
  );
}

// Per-card grid placement.
//
// - state="grid": no explicit placement; the 3x2 grid auto-flows in
//   DIMENSIONS order.
// - state="strip": pack into columns 1-5 in DIMENSIONS order, with the
//   expanded card skipped. Column 6 is always empty — the hole sits at
//   the right edge regardless of which card is expanded. The trade-off
//   versus pinning each card to a fixed column is that more cards shift
//   when switching expanded dimensions, but the empty slot doesn't
//   wander mid-strip.
// - state="expanded": span every column on row 2.
function computeGridPlacement(
  state: CardState,
  cardKey: DimensionKey,
  expandedKey: DimensionKey | null,
): React.CSSProperties {
  if (state === "grid") return {};
  if (state === "expanded") return { gridColumn: "1 / -1", gridRow: "2" };
  const stripIndex = DIMENSIONS.filter((d) => d.key !== expandedKey).findIndex(
    (d) => d.key === cardKey,
  );
  return { gridColumn: stripIndex + 1, gridRow: "1" };
}

export function ResultsColumnIsland(): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <LazyMotion features={domMax} strict>
        {/* Shown above the results whether or not the baseline has loaded yet,
            so a shared-link visitor sees what loaded while the reform auto-runs. */}
        <RestoreNotice />
        <ResultsShell />
      </LazyMotion>
    </QueryClientProvider>
  );
}
